import React, { useState, useEffect, useCallback } from 'react';
import { 
  Link2, Unlink, Loader2, AlertTriangle, RefreshCw,
  CheckCircle, Instagram, Shield, Zap, Image, Video, 
  X, Check, ExternalLink
} from 'lucide-react';
import { socialConnectionsService, PLATFORMS } from '../../services/SocialConnectionsService';

// Ícone do TikTok customizado
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// ============================================================
// MODAL DE CONEXÃO OAUTH
// ============================================================

const OAuthConnectionModal = ({ 
  platform, 
  platformInfo,
  onClose, 
  onSuccess 
}) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const handleConnect = async () => {
    setStatus('connecting');
    setError(null);

    try {
      const result = await socialConnectionsService.connectWithOAuth(platform);
      setStatus('success');
      
      setTimeout(() => {
        onSuccess(result);
      }, 1500);
      
    } catch (err) {
      console.error('OAuth Error:', err);
      setStatus('error');
      setError(err.message || 'Erro ao conectar. Tente novamente.');
    }
  };

  const getCapabilities = (platformKey) => {
    const capabilities = {
      instagram: ['Publicar fotos no feed', 'Publicar vídeos e reels', 'Adicionar legendas'],
      tiktok: ['Publicar vídeos', 'Publicar fotos', 'Configurar privacidade']
    };
    return capabilities[platformKey] || ['Publicar conteúdo'];
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && status !== 'connecting') {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${platformInfo.bgColor} rounded-xl flex items-center justify-center shadow-md`}>
                {platform === 'instagram' ? (
                  <Instagram className="w-6 h-6 text-white" />
                ) : (
                  <TikTokIcon className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Conectar {platformInfo.name}
                </h3>
                <p className="text-sm text-gray-500">Autorização segura via OAuth</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              disabled={status === 'connecting'}
              className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Estado: Inicial */}
          {status === 'idle' && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Autorize o acesso para publicar conteúdos no <strong>{platformInfo.name}</strong>.
              </p>
              
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-2xl p-4 mb-5">
                <h4 className="text-sm font-bold text-amber-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Permissões solicitadas
                </h4>
                <ul className="space-y-2">
                  {getCapabilities(platform).map((cap, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-amber-800">
                      <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={handleConnect}
                className="w-full py-3.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-bold hover:from-amber-500 hover:to-orange-600 transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5"
              >
                <ExternalLink className="w-5 h-5" />
                Conectar conta
              </button>
              
              <p className="text-xs text-gray-400 text-center mt-4">
                Uma nova janela será aberta para autorização
              </p>
            </>
          )}

          {/* Estado: Conectando */}
          {status === 'connecting' && (
            <div className="text-center py-8">
              <div className={`w-16 h-16 ${platformInfo.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                {platform === 'instagram' ? (
                  <Instagram className="w-8 h-8 text-white" />
                ) : (
                  <TikTokIcon className="w-8 h-8 text-white" />
                )}
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Aguardando autorização
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Complete a conexão na janela aberta
              </p>
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Verificando...</span>
              </div>
              <p className="text-xs text-amber-600 mt-6 bg-amber-50 p-3 rounded-xl">
                Se nenhuma janela abriu, verifique se popups estão permitidos
              </p>
            </div>
          )}

          {/* Estado: Sucesso */}
          {status === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Conectado com sucesso!
              </h4>
              <p className="text-sm text-gray-500">
                Sua conta do {platformInfo.name} foi vinculada
              </p>
            </div>
          )}

          {/* Estado: Erro */}
          {status === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Falha na conexão
              </h4>
              <p className="text-sm text-red-600 mb-5">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setStatus('idle'); setError(null); }}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-bold hover:from-amber-500 hover:to-orange-600 transition-all"
                >
                  Tentar novamente
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all"
                >
                  Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PÁGINA PRINCIPAL
// ============================================================

const SocialConnectionsPage = () => {
  const [connections, setConnections] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disconnectingPlatform, setDisconnectingPlatform] = useState(null);
  
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  const fetchConnections = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await socialConnectionsService.getConnections();
      
      if (response.platforms) {
        setConnections(response.platforms);
      } else if (response.connections && Array.isArray(response.connections)) {
        const platformsObj = {};
        response.connections.forEach(conn => {
          platformsObj[conn.platform] = {
            ...conn,
            is_connected: conn.status === 'active'
          };
        });
        setConnections(platformsObj);
      } else {
        setConnections(response || {});
      }
    } catch (err) {
      console.error('Erro ao buscar conexões:', err);
      setError(err.response?.data?.detail || err.message || 'Não foi possível carregar as conexões.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleConnect = (platform) => {
    setSelectedPlatform(platform);
    setShowOAuthModal(true);
  };

  const handleOAuthSuccess = (result) => {
    setConnections(prev => ({
      ...prev,
      [result.platform]: {
        ...prev[result.platform],
        ...result,
        is_connected: true,
        status: 'active',
        connected_at: result.connected_at || new Date().toISOString()
      }
    }));

    setShowOAuthModal(false);
    setSelectedPlatform(null);

    setTimeout(() => {
      fetchConnections();
    }, 500);
  };

  const handleDisconnect = async (platform) => {
    const platformName = PLATFORMS[platform]?.name || platform;
    
    const confirmDisconnect = window.confirm(
      `Deseja desconectar sua conta do ${platformName}?`
    );

    if (!confirmDisconnect) return;

    setDisconnectingPlatform(platform);
    setError(null);

    try {
      await socialConnectionsService.disconnect(platform);
      
      setConnections(prev => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          is_connected: false,
          status: 'disconnected',
          username: null,
          connected_at: null
        }
      }));
    } catch (err) {
      console.error('Erro ao desconectar:', err);
      setError(err.response?.data?.detail || `Erro ao desconectar ${platformName}`);
    } finally {
      setDisconnectingPlatform(null);
    }
  };

  const renderPlatformCard = (platformKey) => {
    const platform = PLATFORMS[platformKey];
    const connection = connections[platformKey] || {};
    const isConnected = connection.is_connected || connection.status === 'active';
    const isDisconnecting = disconnectingPlatform === platformKey;

    return (
      <div
        key={platformKey}
        className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all"
      >
        {/* Header com gradiente da plataforma */}
        <div className={`${platform.bgColor} p-6 text-white`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md">
              {platformKey === 'instagram' ? (
                <Instagram className="w-7 h-7 text-white" />
              ) : (
                <TikTokIcon className="w-7 h-7 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold">{platform.name}</h3>
              <p className="text-white/80 text-sm">{platform.description}</p>
            </div>
          </div>
        </div>

        {/* Conteúdo do card */}
        <div className="p-6">
          {isConnected ? (
            <>
              {/* Status conectado */}
              <div className="flex items-center gap-3 mb-5 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-green-700 text-sm">Conectado</p>
                  {connection.username && (
                    <p className="text-xs text-green-600">@{connection.username}</p>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2.5 mb-5">
                {platform.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Botão desconectar */}
              <button
                onClick={() => handleDisconnect(platformKey)}
                disabled={isDisconnecting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
              >
                {isDisconnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Desconectando...
                  </>
                ) : (
                  <>
                    <Unlink className="w-4 h-4" />
                    Desconectar
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Features disponíveis */}
              <div className="space-y-2.5 mb-5">
                {platform.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-500">
                    <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Tipos suportados */}
              <div className="flex items-center gap-2 mb-5">
                {platform.supportedTypes.includes('image') && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl text-xs font-bold text-amber-700 border border-amber-100">
                    <Image className="w-3.5 h-3.5" /> Imagens
                  </span>
                )}
                {platform.supportedTypes.includes('video') && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl text-xs font-bold text-orange-700 border border-orange-100">
                    <Video className="w-3.5 h-3.5" /> Vídeos
                  </span>
                )}
              </div>

              {/* Info segurança */}
              <div className="flex items-center gap-2 mb-5 text-xs text-gray-400">
                <Shield className="w-4 h-4" />
                <span>Conexão segura via OAuth</span>
              </div>

              {/* Botão conectar */}
              <button
                onClick={() => handleConnect(platformKey)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-bold hover:from-amber-500 hover:to-orange-600 transition-all shadow-md hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5"
              >
                <Link2 className="w-4 h-4" />
                Conectar
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Modal OAuth */}
      {showOAuthModal && selectedPlatform && (
        <OAuthConnectionModal
          platform={selectedPlatform}
          platformInfo={PLATFORMS[selectedPlatform]}
          onClose={() => {
            setShowOAuthModal(false);
            setSelectedPlatform(null);
          }}
          onSuccess={handleOAuthSuccess}
        />
      )}

      <div className="w-full max-w-4xl">
        {/* Erro */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl flex items-start mb-6">
            <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-bold text-sm">Ops! Algo deu errado</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)} 
              className="ml-3 p-1.5 hover:bg-red-100 rounded-xl transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mb-4">
              <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
            </div>
            <p className="text-gray-500 font-medium">Carregando conexões...</p>
          </div>
        )}

        {/* Grid de plataformas centralizado */}
        {!isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {renderPlatformCard('instagram')}
            {renderPlatformCard('tiktok')}
          </div>
        )}

        {/* Botão de refresh discreto */}
        {!isLoading && (
          <div className="flex justify-center mt-6">
            <button
              onClick={fetchConnections}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-amber-600 hover:bg-white rounded-xl transition-all border border-transparent hover:border-gray-200"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialConnectionsPage;